// ============== InviteSignupPage.jsx (อัพเดตเฉพาะส่วน state/logic ที่เกี่ยวข้อง)
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Mail, User, UserCircle2, X, CheckCircle2 } from 'lucide-react'
import { fetchInviteInfoGuest, checkLearnerExists, addLearnerGuest } from '@/api/kuizy'
import ConfirmModal from '@/widgets/ConfirmModal'

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function InviteSignupPage() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ open: false, msg: '', type: '' })
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', nick_name: '' })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  // แทน boolean เดิม ให้เก็บข้อมูลการชน (matched) กลับมาจาก API
  const [existInfo, setExistInfo] = useState(null) // { exists, matched: 'email'|'username', ... }

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchInviteInfoGuest(token)
        if (res.data.error) throw new Error(res.data.error)
        setInvite(res.data)
      } catch {
        setAlert({ open: true, msg: 'ขออภัย: ลิงก์คำเชิญไม่ถูกต้องหรือหมดอายุ', type: 'error' })
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  const validateEmail = () => {
    const errs = {}
    const v = (form.email || '').trim()
    if (!v) errs.email = 'โปรดกรอกอีเมลของคุณ'
    else if (!emailRx.test(v)) errs.email = 'รูปแบบอีเมลไม่ถูกต้อง'
    return errs
  }

  const handleEmailNext = async e => {
    e.preventDefault()
    const errs = validateEmail()
    if (Object.keys(errs).length) { setErrors(errs); return }
    const normalized = form.email.trim().toLowerCase()
    setForm(f => ({ ...f, email: normalized }))
    setSubmitting(true)
    try {
      const res = await checkLearnerExists(normalized)
      if (res.data?.exists) {
        setExistInfo(res.data) // เก็บ matched
        setStep(1)
      } else {
        setExistInfo(null)
        setStep(2)
      }
    } catch {
      setAlert({ open: true, msg: 'ไม่สามารถตรวจสอบได้ กรุณาลองใหม่ภายหลัง', type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const validateDetails = () => {
    const errs = {}
    if (!form.first_name.trim()) errs.first_name = 'โปรดกรอกชื่อจริง'
    if (!form.last_name.trim()) errs.last_name = 'โปรดกรอกนามสกุล'
    if (!form.nick_name.trim()) errs.nick_name = 'โปรดกรอกชื่อเล่น'
    return errs
  }

  const handleDetailsSubmit = async e => {
    e.preventDefault()
    const errs = validateDetails()
    if (Object.keys(errs).length) { setErrors(errs); return }
    if (invite.quota && invite.used >= invite.quota) {
      setAlert({ open: true, msg: 'ขออภัย: โควต้าคำเชิญเต็มแล้ว', type: 'error' })
      return
    }
    const normalized = form.email.trim().toLowerCase()
    setForm(f => ({ ...f, email: normalized }))

    setSubmitting(true)
    try {
      // re-check กัน race
      const chk = await checkLearnerExists(normalized)
      if (chk.data?.exists) {
        setExistInfo(chk.data)
        setStep(1)
        setAlert({ open: true, msg: `พบ${chk.data.matched === 'username' ? 'ชื่อผู้ใช้' : 'อีเมล'}นี้ในระบบแล้ว`, type: 'error' })
        return
      }
      const res = await addLearnerGuest(token, { ...form, email: normalized })
      if (res.data?.error) throw new Error(res.data.error)
      setAlert({ open: true, msg: 'ลงทะเบียนเรียบร้อย! โปรดตรวจสอบอีเมลเพื่อยืนยันบัญชี', type: 'success' })
    } catch (err) {
      setAlert({ open: true, msg: err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง', type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p className="text-center mt-8">กำลังตรวจสอบคำเชิญ กรุณารอสักครู่...</p>
  if (!invite) return null

  if (alert.open && alert.type === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-info via-primary/20 to-info/30 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center space-y-4">
          <CheckCircle2 className="w-12 h-12 text-success mx-auto" />
          <h2 className="text-xl font-semibold">ขอบคุณที่ร่วมลงทะเบียน</h2>
          <p className="text-sm text-gray-600">{alert.msg}</p>
          <button
            className="bg-primary hover:bg-secondary text-white rounded-lg px-5 py-2 text-sm font-semibold w-full"
            onClick={() => navigate('/learn/login', { replace: true })}
          >
            ไปยังหน้าล็อกอิน
          </button>
        </div>
      </div>
    )
  }

  const emailValid = emailRx.test((form.email || '').trim())

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-info via-primary/20 to-info/30 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-neutral-900">เข้าร่วมกลุ่มผู้เรียน: “{invite.group_name || 'Member'}”</h2>
          <button className="text-gray-500 hover:text-primary" onClick={() => navigate(-1)} title="ย้อนกลับ">
            <X size={20}/>
          </button>
        </header>

        <section className="px-6 py-6 space-y-5">
          <p className="text-sm text-gray-600">กรอกอีเมลของคุณ (ระบบจะตรวจทั้ง “อีเมล” และ “ชื่อผู้ใช้” ที่มีอยู่แล้ว)</p>

          {existInfo?.exists ? (
            <div className="text-center space-y-4">
              <p className="text-danger text-base">
                พบ{existInfo.matched === 'username' ? 'ชื่อผู้ใช้' : 'อีเมล'}นี้ในระบบแล้ว: {form.email}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="bg-primary hover:bg-secondary text-white rounded-lg px-5 py-2 text-sm font-semibold w-full"
                  onClick={() => navigate('/learn/login', { replace: true })}
                >
                  ไปยังหน้าล็อกอิน
                </button>
                <button
                  className="border border-gray-300 hover:border-primary text-gray-700 rounded-lg px-5 py-2 text-sm font-semibold w-full"
                  onClick={() => { setExistInfo(null); setStep(1); }}
                >
                  เปลี่ยนอีเมล
                </button>
              </div>
            </div>
          ) : step === 1 ? (
            <form onSubmit={handleEmailNext} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">อีเมล <span className="text-danger">*</span></label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setErrors({}); setExistInfo(null) }}
                    onBlur={() => {
                      const v = (form.email || '').trim().toLowerCase()
                      if (v && v !== form.email) setForm(f => ({ ...f, email: v }))
                    }}
                    className={`border rounded-lg pl-9 pr-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary ${errors.email ? 'border-danger' : 'border-gray-300'}`}
                  />
                </div>
                {errors.email && <small className="text-danger text-xs">{errors.email}</small>}
              </div>
              <button
                className="bg-primary hover:bg-secondary text-white rounded-lg px-5 py-2 text-sm font-semibold w-full disabled:opacity-60"
                type="submit"
                disabled={submitting || !emailValid}
              >
                {submitting ? 'กำลังตรวจสอบ...' : 'ถัดไป'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleDetailsSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">อีเมล</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input type="email" value={form.email} disabled className="border border-gray-200 bg-gray-100 rounded-lg pl-9 pr-3 py-2 text-sm w-full"/>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">ชื่อจริง <span className="text-danger">*</span></label>
                <div className="relative">
                  <User className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input
                    name="first_name"
                    placeholder="ชื่อของคุณ"
                    value={form.first_name}
                    onChange={e => { setForm(f => ({ ...f, first_name: e.target.value })); setErrors(errs => ({ ...errs, first_name: undefined })) }}
                    className={`border rounded-lg pl-9 pr-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary ${errors.first_name ? 'border-danger' : 'border-gray-300'}`}
                  />
                </div>
                {errors.first_name && <small className="text-danger text-xs">{errors.first_name}</small>}
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">นามสกุล <span className="text-danger">*</span></label>
                <div className="relative">
                  <UserCircle2 className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input
                    name="last_name"
                    placeholder="นามสกุลของคุณ"
                    value={form.last_name}
                    onChange={e => { setForm(f => ({ ...f, last_name: e.target.value })); setErrors(errs => ({ ...errs, last_name: undefined })) }}
                    className={`border rounded-lg pl-9 pr-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary ${errors.last_name ? 'border-danger' : 'border-gray-300'}`}
                  />
                </div>
                {errors.last_name && <small className="text-danger text-xs">{errors.last_name}</small>}
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium">ชื่อเล่น <span className="text-danger">*</span></label>
                <div className="relative">
                  <UserCircle2 className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input
                    name="nick_name"
                    placeholder="ชื่อเล่นของคุณ"
                    value={form.nick_name}
                    onChange={e => { setForm(f => ({ ...f, nick_name: e.target.value })); setErrors(errs => ({ ...errs, nick_name: undefined })) }}
                    className={`border rounded-lg pl-9 pr-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary ${errors.nick_name ? 'border-danger' : 'border-gray-300'}`}
                  />
                </div>
                {errors.nick_name && <small className="text-danger text-xs">{errors.nick_name}</small>}
              </div>

              <button
                className="bg-primary hover:bg-secondary text-white rounded-lg px-5 py-2 text-sm font-semibold w-full disabled:opacity-60"
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'กำลังลงทะเบียน...' : 'ลงทะเบียน และรับลิงก์'}
              </button>

              {invite.quota != null && (
                <p className="text-xs text-gray-500 mt-1">
                  โควต้า: {invite.quota} คงเหลือ: {invite.quota - invite.used}
                </p>
              )}
            </form>
          )}

          {alert.open && alert.type === 'error' && (
            <ConfirmModal
              type="alert"
              title="แจ้งเตือน"
              message={alert.msg}
              onClose={() => setAlert({ open: false, msg: '', type: '' })}
            />
          )}
        </section>
      </div>
    </div>
  )
}
